import { AxiosError } from 'axios';
import {
  useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult,
} from 'react-query';
import {
  endReferralPosition,
  forceSendReferralReward,
  getAccountReferrals,
  getReferrals,
  overrideReferralEligibility,
  qualifyReferral,
  recheckReferralEligibility,
  resendReferralReward,
  updateReferralPayingOutsideStripe,
  updateReferredAccount,
} from '../../api/referralsApi';
import PaginationResponseDto from '../../interfaces/paginationResponse.interface';
import {
  AccountReferrals, Referral, ReferralForceSendResult, ReferralsQuery,
} from '../../@types/Referral';

const REFERRALS_QUERY_KEY = 'referrals';
const ACCOUNT_REFERRALS_QUERY_KEY = 'account-referrals';

export const useGetReferrals = (
  query: ReferralsQuery,
): UseQueryResult<PaginationResponseDto<Referral>, AxiosError> => {
  return useQuery<PaginationResponseDto<Referral>, AxiosError>(
    [REFERRALS_QUERY_KEY, query],
    () => getReferrals(query),
    {
      keepPreviousData: true,
      retry: false,
    },
  );
};

export const useGetAccountReferrals = (accountId: string): UseQueryResult<AccountReferrals, AxiosError> => {
  return useQuery<AccountReferrals, AxiosError>(
    [ACCOUNT_REFERRALS_QUERY_KEY, accountId],
    () => getAccountReferrals(accountId),
    {
      enabled: !!accountId,
      retry: false,
    },
  );
};

type OverrideEligibilityParams = { referralId: string, reason: string };
type ReferredAccountParams = { referralId: string, accountId: string | null };
type PayingOutsideStripeParams = { referralId: string, payingOutsideStripe: boolean };
type ResendRewardParams = { referralRewardId: string, newEmail?: string };

type ReferralMutation<TParams> = UseMutationResult<void, AxiosError, TParams, unknown>;

export type ReferralActions = {
  overrideEligibility: ReferralMutation<OverrideEligibilityParams>,
  recheckEligibility: ReferralMutation<string>,
  updateReferredAccount: ReferralMutation<ReferredAccountParams>,
  updatePayingOutsideStripe: ReferralMutation<PayingOutsideStripeParams>,
  qualify: ReferralMutation<string>,
  resendReward: ReferralMutation<ResendRewardParams>,
  forceSendReward: UseMutationResult<ReferralForceSendResult, AxiosError, string, unknown>,
  endPosition: ReferralMutation<string>,
};

/**
 * Every back-office referral action. Each refreshes the referrals list and the account panel's referral block.
 */
export const useReferralActions = (): ReferralActions => {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries(REFERRALS_QUERY_KEY);
    queryClient.invalidateQueries(ACCOUNT_REFERRALS_QUERY_KEY);
  };

  return {
    overrideEligibility: useMutation<void, AxiosError, OverrideEligibilityParams>(
      ({ referralId, reason }) => overrideReferralEligibility(referralId, reason),
      { onSuccess },
    ),
    recheckEligibility: useMutation<void, AxiosError, string>(
      (referralId) => recheckReferralEligibility(referralId),
      { onSuccess },
    ),
    updateReferredAccount: useMutation<void, AxiosError, ReferredAccountParams>(
      ({ referralId, accountId }) => updateReferredAccount(referralId, accountId),
      { onSuccess },
    ),
    updatePayingOutsideStripe: useMutation<void, AxiosError, PayingOutsideStripeParams>(
      ({ referralId, payingOutsideStripe }) => updateReferralPayingOutsideStripe(referralId, payingOutsideStripe),
      { onSuccess },
    ),
    qualify: useMutation<void, AxiosError, string>(
      (referralId) => qualifyReferral(referralId),
      { onSuccess },
    ),
    resendReward: useMutation<void, AxiosError, ResendRewardParams>(
      ({ referralRewardId, newEmail }) => resendReferralReward(referralRewardId, newEmail),
      { onSuccess },
    ),
    forceSendReward: useMutation<ReferralForceSendResult, AxiosError, string>(
      (referralRewardId) => forceSendReferralReward(referralRewardId),
      { onSuccess },
    ),
    endPosition: useMutation<void, AxiosError, string>(
      (referralPositionId) => endReferralPosition(referralPositionId),
      { onSuccess },
    ),
  };
};
